import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Trash2, Copy, Plus } from 'lucide-react';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const TYPE_LABELS = {
  casal: 'Casal',
  solteira: 'Solteira',
  solteiro: 'Solteiro',
};

const AdminSocialInvites = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [chosenType, setChosenType] = useState('casal');
  const [createdInvite, setCreatedInvite] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/social-invites`, { headers: authHeaders() });
      setInvites(res.data.invites || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar convites sociais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    load();
  }, [user, navigate, load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/admin/social-invites`,
        { invite_type: chosenType },
        { headers: authHeaders() }
      );
      setCreatedInvite(res.data);
      toast.success('Convite social criado com sucesso!');
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao criar convite');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deletar este convite não utilizado?')) return;
    try {
      await axios.delete(`${API}/admin/social-invites/${id}`, { headers: authHeaders() });
      toast.success('Convite deletado');
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao deletar');
    }
  };

  const buildInviteUrl = (token) => `${BASE_URL}/?social_invite=${token}`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copiado!'),
      () => toast.error('Falha ao copiar')
    );
  };

  return (
    <div data-testid="admin-social-invites-page" className="min-h-screen bg-[#0D0D0D] text-white p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 rounded-lg hover:bg-white/5"
              data-testid="admin-social-invites-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#C79F5E]">Convites Sociais</h1>
              <p className="text-gray-400 text-sm mt-1">Gere convites para a Rede Social (Casais / Solteiras / Solteiros).</p>
            </div>
          </div>
          <button
            onClick={load}
            className="text-xs bg-[#1A1A1A] border border-[#C79F5E]/40 hover:border-[#C79F5E] text-[#C79F5E] px-3 py-2 rounded-lg font-semibold"
            data-testid="admin-social-invites-refresh"
          >
            Atualizar
          </button>
        </div>

        {/* Criar novo convite */}
        <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-4 mb-6">
          <p className="text-sm font-semibold text-white mb-3">Criar novo convite</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <select
              data-testid="social-invite-type"
              value={chosenType}
              onChange={(e) => setChosenType(e.target.value)}
              className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="casal">Casal</option>
              <option value="solteira">Solteira</option>
              <option value="solteiro">Solteiro</option>
            </select>
            <button
              onClick={handleCreate}
              disabled={creating}
              data-testid="social-invite-create-btn"
              className="flex items-center gap-2 bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black font-bold px-4 py-2 rounded-lg hover:scale-[1.02] disabled:opacity-50 transition-all"
            >
              <Plus className="w-4 h-4" /> {creating ? 'Criando...' : 'Gerar Convite'}
            </button>
          </div>

          {createdInvite && (
            <div data-testid="created-invite-box" className="mt-3 p-3 bg-[#0D0D0D] border border-[#C79F5E]/50 rounded-xl">
              <p className="text-xs text-gray-400">Link do convite ({TYPE_LABELS[createdInvite.invite_type]}):</p>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-xs bg-black/40 px-2 py-1.5 rounded break-all">
                  {buildInviteUrl(createdInvite.invite_token)}
                </code>
                <button
                  onClick={() => copyToClipboard(buildInviteUrl(createdInvite.invite_token))}
                  className="p-2 bg-[#C79F5E]/20 hover:bg-[#C79F5E]/30 rounded-lg text-[#C79F5E]"
                  data-testid="copy-created-link"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Carregando...</div>
        ) : invites.length === 0 ? (
          <div data-testid="social-invites-empty" className="text-center text-gray-400 py-16 border border-[#2A2A2A] rounded-2xl bg-[#111]">
            Nenhum convite social criado ainda.
          </div>
        ) : (
          <div className="space-y-2">
            {invites.map((inv) => (
              <div
                key={inv.id}
                data-testid={`social-invite-row-${inv.id}`}
                className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-[#C79F5E]/20 text-[#C79F5E] text-xs font-bold px-2 py-0.5 rounded-full">
                      {TYPE_LABELS[inv.invite_type]}
                    </span>
                    {inv.used ? (
                      <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded-full">
                        usado
                      </span>
                    ) : (
                      <span className="bg-yellow-500/20 text-yellow-300 text-xs font-bold px-2 py-0.5 rounded-full">
                        disponível
                      </span>
                    )}
                    <code className="text-xs text-gray-300 truncate">{inv.token}</code>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Criado: {inv.created_at ? new Date(inv.created_at).toLocaleString('pt-BR') : '—'}
                    {inv.used_by_user && (
                      <span className="ml-2">
                        • Usado por <strong className="text-white">{inv.used_by_user.name || inv.used_by_user.email}</strong>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 sm:justify-end">
                  <button
                    onClick={() => copyToClipboard(buildInviteUrl(inv.token))}
                    className="px-3 py-2 rounded-lg bg-[#C79F5E]/20 hover:bg-[#C79F5E]/30 text-[#C79F5E] text-xs font-semibold flex items-center gap-1"
                    data-testid={`social-invite-copy-${inv.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar link
                  </button>
                  {!inv.used && (
                    <button
                      onClick={() => handleDelete(inv.id)}
                      className="px-3 py-2 rounded-lg bg-red-900/30 hover:bg-red-900/50 text-red-300 text-xs font-semibold flex items-center gap-1"
                      data-testid={`social-invite-delete-${inv.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSocialInvites;
